import {
  Injectable,
  computed,
  inject,
  signal
} from '@angular/core';
import { Observable, tap } from 'rxjs';

import { Notifier } from '@core/notifications';
import { listenWebsocketEvents } from '@core/websocket';
import { SoundsService } from '@core/sounds';

import {
  Notification,
  ApiResponse,
  NotificationType,
  QuestCompletedContent
} from './notifications.entity';
import { NotificationsService } from './notifications.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationsState {
  private readonly notificationsService = inject(NotificationsService);
  private readonly notifier = inject(Notifier);
  private readonly soundsService = inject(SoundsService);

  public readonly notifications = signal<Notification[]>([]);
  public readonly historyNotifications = signal<Notification[]>([]);

  public readonly unreadImportantNotifications = computed(() => {
    const notifications = this.notifications();
    const importantNotifications = notifications
      .filter((notification) => notification.isImportant && !notification.isRead && !this.openNotifications.includes(notification.id));

    importantNotifications.forEach((notification) => this.openNotifications.push(notification.id));

    return importantNotifications;
  });

  public readonly hasUnread = computed(() => !!this.notifications().find((notification) => !notification.isRead));

  private readonly openNotifications: number[] = []; // TODO this might leak memory, do something

  private readonly wsEvents = listenWebsocketEvents({
    'users.new_notification': (data) => this.onWsNewNotification(data)
  });

  loadUnread(): Observable<ApiResponse.Notifications> {
    return this.notificationsService.getUnread().pipe(
      tap((response: ApiResponse.Notifications) => {
        this.notifications.set(response.notifications);
      })
    );
  }

  loadHistory(): Observable<ApiResponse.Notifications> {
    return this.notificationsService.getHistory().pipe(
      tap((response: ApiResponse.Notifications) => {
        this.historyNotifications.set(response.notifications);
      })
    );
  }

  markNotificationAsRead(id: number): Observable<void> {
    return this.notificationsService.markAsRead([ id ]).pipe(
      tap(() => {
        this.notifications.update((notifications) => {
          const index = notifications.findIndex((row) => row.id === id);

          notifications[index] = {
            ...notifications[index],
            isRead: true
          };

          return [...notifications];
        });
      })
    );
  }

  readAll(): Observable<void> {
    const ids = this.notifications().map((row) => row.id);

    return this.notificationsService.markAsRead(ids).pipe(
      tap(() => {
        this.notifications.update((notifications) => notifications.filter(notification => !ids.includes(notification.id)));
      })
    );
  }

  private onWsNewNotification(notification: Notification): void {
    this.notifications.set([...this.notifications(), notification]);

    this.soundsService.playSound('new-notification');

    if(NotificationType.QUEST_COMPLETED === notification.type) {
      const { title } = notification.content as QuestCompletedContent;

      this.notifier.success(
        $localize`:@@notifications.quest_completed.notification:Квест "${ title }" завершен!`
      );
    }
  }
}
